import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PLATFORM_PRIMARY, PLATFORM_SECONDARY } from '../../theme/brand';
import { setActiveCurrency, setActiveCurrencyFormat, type CurrencyDisplayFormat } from '../../utils/currency';

interface ModuleConfig {
  key: string;
  enabled: boolean;
}

interface TenantBranding {
  primaryColor: string;
  secondaryColor: string;
  logo?: string;
  favicon?: string;
}

interface TenantState {
  id: string | null;
  companyName: string;
  slug: string;
  branding: TenantBranding;
  modules: ModuleConfig[];
  enabledModuleKeys: string[];
  enabledFeatures: string[];
  currency: string;
  currencyFormat: CurrencyDisplayFormat | null;
  isDemo: boolean;
  demoExpiresAt: string | null;
}

function deriveEnabledModuleKeys(modules: ModuleConfig[]): string[] {
  return modules.filter((m) => m.enabled).map((m) => m.key);
}

const initialState: TenantState = {
  id: null,
  companyName: '',
  slug: localStorage.getItem('tenantSlug') || 'demo',
  branding: { primaryColor: PLATFORM_PRIMARY, secondaryColor: PLATFORM_SECONDARY },
  modules: [],
  enabledModuleKeys: [],
  enabledFeatures: JSON.parse(localStorage.getItem('enabledFeatures') || '[]'),
  currency: localStorage.getItem('tenantCurrency') || '',
  currencyFormat: readStoredCurrencyFormat(),
  isDemo: localStorage.getItem('tenantIsDemo') === 'true',
  demoExpiresAt: null,
};

function readStoredCurrencyFormat(): CurrencyDisplayFormat | null {
  try {
    const raw = localStorage.getItem('tenantCurrencyFormat');
    return raw ? JSON.parse(raw) as CurrencyDisplayFormat : null;
  } catch {
    return null;
  }
}

setActiveCurrencyFormat(initialState.currencyFormat);
if (initialState.currency) {
  setActiveCurrency(initialState.currency);
}

const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    setTenant: (state, action: PayloadAction<Partial<TenantState> & { id?: string }>) => {
      const payload = { ...action.payload };
      if (payload.branding) {
        payload.branding = {
          primaryColor: payload.branding.primaryColor || state.branding.primaryColor || PLATFORM_PRIMARY,
          secondaryColor: payload.branding.secondaryColor || state.branding.secondaryColor || PLATFORM_SECONDARY,
          logo: payload.branding.logo ?? state.branding.logo,
          favicon: payload.branding.favicon ?? state.branding.favicon,
        };
      }
      Object.assign(state, payload);
      if (payload.modules) {
        state.enabledModuleKeys = deriveEnabledModuleKeys(payload.modules);
      }
      if (payload.slug) {
        localStorage.setItem('tenantSlug', payload.slug);
      }
      if (typeof payload.isDemo === 'boolean') {
        localStorage.setItem('tenantIsDemo', payload.isDemo ? 'true' : 'false');
      }
      if (payload.enabledFeatures) {
        localStorage.setItem('enabledFeatures', JSON.stringify(payload.enabledFeatures));
      }
      if (payload.currency !== undefined) {
        const next = String(payload.currency || '').trim();
        if (next) localStorage.setItem('tenantCurrency', next);
        else localStorage.removeItem('tenantCurrency');
        setActiveCurrency(next);
      }
      if (payload.currencyFormat !== undefined) {
        if (payload.currencyFormat) localStorage.setItem('tenantCurrencyFormat', JSON.stringify(payload.currencyFormat));
        else localStorage.removeItem('tenantCurrencyFormat');
        setActiveCurrencyFormat(payload.currencyFormat);
      }
    },
    clearTenant: () => {
      localStorage.removeItem('tenantCurrency');
      localStorage.removeItem('tenantCurrencyFormat');
      setActiveCurrencyFormat(null);
      setActiveCurrency('');
      return { ...initialState, currency: '', currencyFormat: null };
    },
  },
});

export const { setTenant, clearTenant } = tenantSlice.actions;
export default tenantSlice.reducer;

export const selectEnabledModules = (state: { tenant: TenantState }) => state.tenant.enabledModuleKeys;

export const selectTenantSlug = (state: { tenant: TenantState }) => state.tenant.slug || 'demo';

export const selectTenantCurrency = (state: { tenant: TenantState }) => state.tenant.currency || '';

export const selectTenantCurrencyFormat = (state: { tenant: TenantState }) => state.tenant.currencyFormat;

export const selectEnabledFeatures = (state: { tenant: TenantState }) =>
  state.tenant.enabledFeatures;

export const isModuleEnabled = (state: { tenant: TenantState }, moduleKey: string) =>
  state.tenant.enabledModuleKeys.includes(moduleKey);

export const isFeatureEnabled = (state: { tenant: TenantState }, featureKey: string) =>
  state.tenant.enabledFeatures.includes(featureKey);
